// components/Profile/TaxPosition.jsx
// Tax Position Questions - PILOT VERSION
// Per Mark's decision (March 2026):
// - ISA allowance usage ✓
// - High earner warning at £200k (configurable) ✓
// - DROPPED: Carry forward (too complex for pilot)
// - Debt comfort ✓
// NOTE: Pension contributions are captured in ContributionDetails.jsx (not here)

import React from 'react';
import { Form, Row, Col, InputGroup, Alert, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Configurable threshold - can be changed per provider
const HIGH_EARNER_THRESHOLD = 200000;

const TaxPosition = ({ profile, onChange, contributionDetails, salary }) => {

    const handleChange = (field, value) => {
        onChange({ ...profile, [field]: value });
    };

    // Calculate if high earner warning should show
    const totalIncome = (profile.annual_income || 0) + (profile.other_income || 0);
    const isHighEarner = totalIncome >= HIGH_EARNER_THRESHOLD;

    // Calculate remaining allowances
    const remainingIsa = Math.max(0, 20000 - (profile.current_isa_contributions || 0));

    // Calculate pension contributions from contribution step (Step 3)
    // This gives us the actual amounts rather than relying on tax position state
    const actualSalary = salary || profile.annual_income || 0;
    const employeePct = contributionDetails?.employeeContribution || 0;
    const employerPct = contributionDetails?.employerContribution || 0;
    const annualEmployeeContrib = Math.round(actualSalary * employeePct / 100);
    const annualEmployerContrib = Math.round(actualSalary * employerPct / 100);
    const totalPensionContribs = annualEmployeeContrib + annualEmployerContrib;

    // Standard allowance (high earner warning shown but we don't calc tapered for pilot)
    const baseAllowance = 60000;
    const remainingPension = Math.max(0, baseAllowance - totalPensionContribs);

    return (
        <div className="tax-position-section">
            <div className="section-header mb-4">
                <h5>
                    <FontAwesomeIcon icon="file-invoice-dollar" className="me-2 text-primary" />
                    Tax Position
                </h5>
                <p className="text-muted mb-0">
                    Understanding your current tax wrapper usage helps us optimise your contributions.
                </p>
            </div>

            <Row>
                {/* ================================================================= */}
                {/* ISA ALLOWANCE USAGE */}
                {/* ================================================================= */}
                <Col md={6}>
                    <div className="profile-card">
                        <div className="card-icon success">
                            <FontAwesomeIcon icon="piggy-bank" />
                        </div>
                        <Form.Group className="mb-3">
                            <label>
                                How much have you contributed to ISAs this tax year?
                                <span className="text-muted ms-2">(Allowance: £20,000)</span>
                            </label>
                            <InputGroup>
                                <InputGroup.Text>£</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={profile.current_isa_contributions ?? ''}
                                    onChange={(e) => handleChange('current_isa_contributions', e.target.value === '' ? '' : parseInt(e.target.value))}
                                    onBlur={(e) => { if (e.target.value === '') handleChange('current_isa_contributions', 0); }}
                                    min={0}
                                    max={20000}
                                    placeholder="0"
                                />
                                <InputGroup.Text className="bg-light">
                                    / £20,000
                                </InputGroup.Text>
                            </InputGroup>
                            {remainingIsa > 0 && (
                                <div className="mt-2">
                                    <small className="text-success">
                                        <FontAwesomeIcon icon="check-circle" className="me-1" />
                                        £{remainingIsa.toLocaleString()} ISA allowance remaining
                                    </small>
                                </div>
                            )}
                            {remainingIsa === 0 && (
                                <div className="mt-2">
                                    <small className="text-info">
                                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                                        ISA allowance fully used this year
                                    </small>
                                </div>
                            )}
                        </Form.Group>
                    </div>
                </Col>

                {/* ================================================================= */}
                {/* OTHER INCOME (for high earner check) */}
                {/* ================================================================= */}
                <Col md={6}>
                    <div className="profile-card">
                        <div className="card-icon warning">
                            <FontAwesomeIcon icon="coins" />
                        </div>
                        <Form.Group className="mb-3">
                            <label>Do you have income from other sources?</label>
                            <InputGroup>
                                <InputGroup.Text>£</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={profile.other_income ?? ''}
                                    onChange={(e) => handleChange('other_income', e.target.value === '' ? '' : parseInt(e.target.value))}
                                    onBlur={(e) => { if (e.target.value === '') handleChange('other_income', 0); }}
                                    min={0}
                                    placeholder="0"
                                />
                                <InputGroup.Text>per year</InputGroup.Text>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Dividends, rental income, interest, etc. (excluding salary)
                            </Form.Text>
                        </Form.Group>
                    </div>
                </Col>

                {/* ================================================================= */}
                {/* HIGH EARNER WARNING (£200k threshold - configurable) */}
                {/* ================================================================= */}
                {isHighEarner && (
                    <Col md={12}>
                        <Alert variant="warning" className="d-flex align-items-start">
                            <FontAwesomeIcon
                                icon="exclamation-triangle"
                                className="me-3 mt-1 fa-lg"
                            />
                            <div>
                                <strong>Tapered Annual Allowance Warning</strong>
                                <p className="mb-0 mt-1">
                                    Your total income of <strong>£{totalIncome.toLocaleString()}</strong> exceeds
                                    the £{HIGH_EARNER_THRESHOLD.toLocaleString()} threshold. Your annual pension
                                    allowance may be reduced from £60,000 to as low as £10,000.
                                </p>
                                <p className="mb-0 mt-2 small">
                                    <FontAwesomeIcon icon="info-circle" className="me-1" />
                                    We recommend seeking specialist advice on your pension contribution limits.
                                </p>
                            </div>
                        </Alert>
                    </Col>
                )}

                {/* ================================================================= */}
                {/* DEBT COMFORT */}
                {/* ================================================================= */}
                <Col md={6}>
                    <div className="profile-card">
                        <div className="card-icon danger">
                            <FontAwesomeIcon icon="credit-card" />
                        </div>
                        <Form.Group className="mb-3">
                            <label>Total debt (excluding mortgage)</label>
                            <InputGroup className="mb-2">
                                <InputGroup.Text>£</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={profile.total_debt_excluding_mortgage ?? ''}
                                    onChange={(e) => handleChange('total_debt_excluding_mortgage', e.target.value === '' ? '' : parseInt(e.target.value))}
                                    onBlur={(e) => { if (e.target.value === '') handleChange('total_debt_excluding_mortgage', 0); }}
                                    min={0}
                                    placeholder="0"
                                />
                            </InputGroup>
                            <Form.Check
                                type="checkbox"
                                id="comfortable_with_debt"
                                label="I am comfortable with my current debt levels"
                                checked={profile.comfortable_with_debt !== false}
                                onChange={(e) => handleChange('comfortable_with_debt', e.target.checked)}
                            />
                        </Form.Group>
                    </div>
                </Col>

                {/* Empty column for layout balance */}
                <Col md={6}></Col>
            </Row>

            {/* ================================================================= */}
            {/* SUMMARY CARD */}
            {/* ================================================================= */}
            <Card className="mt-4 bg-light">
                <Card.Body>
                    <h6 className="mb-3">
                        <FontAwesomeIcon icon="calculator" className="me-2 text-primary" />
                        Allowance Summary
                    </h6>
                    <Row>
                        <Col md={4}>
                            <div className="text-center p-3 bg-white rounded">
                                <div className="text-muted small">ISA Remaining</div>
                                <div className="fs-4 fw-bold text-success">£{remainingIsa.toLocaleString()}</div>
                                <div className="text-muted small">of £20,000</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="text-center p-3 bg-white rounded">
                                <div className="text-muted small">Pension Remaining</div>
                                <div className={`fs-4 fw-bold ${isHighEarner ? 'text-warning' : 'text-primary'}`}>
                                    {isHighEarner ? 'See warning' : `£${remainingPension.toLocaleString()}`}
                                </div>
                                <div className="text-muted small">
                                    {isHighEarner ? 'Tapered allowance may apply' : 'of £60,000'}
                                </div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="text-center p-3 bg-white rounded">
                                <div className="text-muted small">Total Income</div>
                                <div className={`fs-4 fw-bold ${isHighEarner ? 'text-warning' : 'text-dark'}`}>
                                    £{totalIncome.toLocaleString()}
                                </div>
                                <div className="text-muted small">
                                    {isHighEarner ? 'Above £200k threshold' : 'Standard allowance applies'}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </div>
    );
};

export default TaxPosition;